import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { useAnimatedScrollHandler } from 'react-native-reanimated';
import { ModalFlatList } from 'design-system/ModalScrollables';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import Modal from 'utils/packages/Modal';
import RadioButton from 'design-system/RadioButton';
import { ButtonGroup, rem, Spacer } from 'design-system';
import Button, { HEIGHT, HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useColors from 'hooks/useColors';
import { useScrollableModalMaxHeight } from 'hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { usePositions, useStockMeta } from '../hooks/selectors';
import { SelectedPositionFilter } from '../types';
import { isWeb } from '../../../constants';
const keyExtractor = (item) => String(item.assetId);
const AssetRow = ({ assetId, selected, setSelectedAssetId }) => {
    const colors = useColors();
    const { name, iconUrl } = useStockMeta(assetId) || {};
    const onPress = useCallback(() => {
        setSelectedAssetId(assetId);
    }, [assetId, setSelectedAssetId]);
    return (<TouchableHighlight hoverOnLight onCard={isWeb} underlayColor={colors.background.underlay} activeOpacity={1} onPress={onPress}>
      <View style={styles.row}>
        <RadioButton type="single" isChecked={selected}/>
        <Spacer w={16}/>
        <Logo R44 uri={iconUrl}/>
        <Spacer w={16}/>
        <View style={styles.assetName}>
          <Text Text-16>{name}</Text>
        </View>
      </View>
    </TouchableHighlight>);
};
const SelectAssetForSwitchModalContent = ({ onSelect, toAssetId, sharedValue, excludeAssetId, onExploreFullFundList, }) => {
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING + HEIGHT);
    const [selectedAssetId, setSelectedAssetId] = useState(toAssetId);
    const positions = usePositions(SelectedPositionFilter.TOTAL_VALUE);
    const finalPositions = useMemo(() => positions.filter((position) => position.assetId !== excludeAssetId), [excludeAssetId, positions]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        sharedValue.value = handle.contentOffset.y;
    }, []);
    const renderItem = useCallback(({ item }) => (<AssetRow assetId={item.assetId} setSelectedAssetId={setSelectedAssetId} selected={item.assetId === selectedAssetId}/>), [selectedAssetId]);
    const maxHeight = useScrollableModalMaxHeight();
    const style = useMemo(() => [{ maxHeight }, styles.list], [maxHeight]);
    const handleSelect = useCallback(() => {
        if (selectedAssetId) {
            onSelect(selectedAssetId);
        }
        SelectAssetForSwitchModal.hide();
    }, [onSelect, selectedAssetId]);
    return (<>
      <View style={styles.subTitleContainer}>
        <Text TextThin-14 Secondary centered={!isWeb}>
          Pick one of your current investments, or choose from the full fund list.
        </Text>
      </View>
      <ModalFlatList style={style} data={finalPositions} initialNumToRender={8} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} keyExtractor={keyExtractor} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle}/>
      <ButtonGroup type="floating" margins={false}>
        <Button brand title="Pick fund for switching" disabled={!selectedAssetId} onPress={handleSelect}/>
        <Button brandReversed title="Explore full fund list" onPress={onExploreFullFundList}/>
      </ButtonGroup>
    </>);
};
export const SelectAssetForSwitchModal = {
    show: (excludeAssetId, toAssetId, onSelect, sharedValue, onDidHideCallback, onExploreFullFundList) => {
        Modal.show(<SelectAssetForSwitchModalContent onSelect={onSelect} toAssetId={toAssetId} sharedValue={sharedValue} excludeAssetId={excludeAssetId} onExploreFullFundList={onExploreFullFundList}/>, {
            containerStyle: styles.modal,
            title: 'Select fund or ETF',
            scrollable: {
                scrollValue: sharedValue,
            },
            onDidHideCallback,
        });
    },
    hide: (onDidHideCallback) => {
        Modal.hide(onDidHideCallback);
    },
};
const styles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    subTitleContainer: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(8),
        ...(isWeb && { paddingTop: rem(8) }),
    },
    // On web the options sit 8 in from the modal edge, so the hover highlight doesn't touch it
    list: {
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    row: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    assetName: {
        flex: 1,
    },
});
